import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import dayjs from 'dayjs';
import type {
  Attachment as StreamAttachment,
  Channel,
  ChannelResponse,
  DefaultGenerics,
  SearchAPIResponse,
  UserResponse,
  FormatMessageResponse,
  Message as StreamMessage,
  Thread as StreamThread,
  EventHandler,
} from 'stream-chat';
import { DeepPartial } from '@frontend/types';
import { WebsocketEventPayload } from '@frontend/websocket';
import type {
  Message,
  User,
  UserPresence,
  StreamUserMetadata,
  StreamChannelMetadata,
  Conversation,
  ConversationEssentials,
  Attachment,
  ReactionGroup,
  UserStatus,
  Thread,
  ThreadReadData,
} from '../types';
import { CHANNEL_TYPE_TEAM_DESIGNATION, PRIVATE_CHANNEL_PREFIX_DESIGNATION } from './stream-constants';

export const getStreamUserId = ({ orgId, weaveUserId }: { orgId: string; weaveUserId: string }) =>
  `ORG_ID_${orgId}_${weaveUserId}`;
export const getTeamId = (orgId: string) => `ORG_ID_${orgId}`;

export const getUserFullName = (firstName?: string, lastName?: string) => {
  if (!firstName && !lastName) {
    return '';
  }
  return `${firstName ?? ''} ${lastName ?? ''}`.trim();
};

export const sortNamesByLength = (names: User[]) => {
  return [...names].sort(
    (a, b) =>
      getUserFullName(b.firstName, b.lastName).replaceAll(/\s/g, '').length -
      getUserFullName(a.firstName, a.lastName).replaceAll(/\s/g, '').length
  );
};

/** To this point, the message search result object satisfies everything we need,
 * so until we need more from a full message, we can just use the search result constructor
 * */
export const constructMessageFromStreamMessage = (message: Message) => constructMessageFromStreamSearchResult(message);
const getReplyCountTotal = (replyCount: number, deletedReplyCount: number) => replyCount - deletedReplyCount;
/**
 *
 * Converts a stream api search result message to our message type
 */
export const constructMessageFromStreamSearchResult = (
  message: SearchAPIResponse['results'][number]['message']
): Message => {
  const mentionedUsers = message.mentioned_users
    ? sortNamesByLength(message.mentioned_users.map(constructUserFromStreamUser))
    : [];

  return {
    channelId: message.channel?.id ?? message.cid?.replace(`${CHANNEL_TYPE_TEAM_DESIGNATION}:`, '') ?? '',
    createdAt: message.created_at ?? '',
    id: message.id,
    isEdited: !!message.message_text_updated_at,
    isUnread: message.status === 'unread',
    lastUpdated: message.updated_at ?? '',
    userId: message.user?.id ?? '',
    type: message.type,
    text: message.text ?? '',
    parentId: message.parent_id || '',
    // stream will not allow us to update a reply count to zero.
    // instead, they keep track of reply count and deleted reply count separately, so we have to do math to figure out the right number
    replyCount: getReplyCountTotal(message?.reply_count || 0, message?.deleted_reply_count || 0) || 0,
    threadParticipantIds: message.thread_participants?.map((participant) => participant.id) ?? [],
    reactions: constructionReactionsFromStreamMessageSearchResult(message),
    attachments: message.attachments
      ?.map(constructAttachmentFromStreamAttachment)
      .filter((attachment) => ['giphy', 'image'].includes(attachment.type) && attachment.url),
    senderUserId: message.user?.id,
    mentionedUserIds: mentionedUsers.map((user) => user.userID),
    mentionedUsers,
  };
};

type TeamChatWebsocketPayload = Extract<WebsocketEventPayload, { method: NotificationType.TEAM_CHAT }>;
export const convertWeaveWebsocketPayload = (event: TeamChatWebsocketPayload) => {
  const payload = event.params;
  const [firstName, lastName] = payload.message?.user?.name?.split(' ') || ['Unknown', 'User'];
  if (!payload.message?.user?.id || !payload.message?.id || !payload.channelId || !payload.type) {
    throw new Error('Missing required fields in websocket event payload', {
      cause: {
        payload,
      },
    });
  }
  return {
    channelId: payload.channelId,
    channelType: 'channelType' in payload ? payload.channelType : null,
    user: {
      userID: payload.message?.user?.id || '',
      firstName,
      lastName,
      status: {
        presence: {
          online: payload.message?.user?.online || false,
        },
      },
    } satisfies DeepPartial<User>,
    message: {
      id: payload.message?.id || '',
      text: payload.message?.text || '',
      attachments: payload.message.attachments?.map(constructAttachmentFromStreamAttachment) || [],
      mentionedUserIds: payload.message?.mentionedUsers || [],
    } satisfies Partial<Message>,
    reaction: 'reaction' in payload ? payload.reaction : undefined,
  };
};

export const constructStreamMessageFromMessage = (message: Message): StreamMessage => {
  return {
    user_id: message.userId,
    id: message.id,
    type: message.type,
    text: message.text,
    created_at: message.createdAt,
    updated_at: message.lastUpdated,
    reply_count: message.replyCount,
    // thread_participants: message.threadParticipantIds?.map(constructStreamUserFromUser),
    attachments: message.attachments?.map(constructStreamAttachmentFromAttachment),
    reaction_counts: message.reactions?.reduce((acc, reaction) => {
      acc[reaction.name] = reaction.count;
      return acc;
    }, {} as Record<string, number>),
    own_reactions: message.reactions?.filter((reaction) => reaction.hasOwnReaction).map((reaction) => reaction.name),
    mentioned_users: message.mentionedUserIds,
  };
};

export const isValidMessage = (message: Message) => {
  return !!((message.text || message.attachments?.length) && !!message.userId && message.channelId);
};
/**
 *
 * converts the reactions stream api search result message to our reaction type
 */
export const constructionReactionsFromStreamMessageSearchResult = (
  message: SearchAPIResponse['results'][number]['message']
): ReactionGroup[] => {
  type ReactionGroups = Record<
    string,
    {
      count: number;
      first_reaction_at: string;
      last_reaction_at: string;
      sum_scores: number;
    }
  >;
  const reactionGroups = message.reaction_groups as ReactionGroups | undefined;
  if (!reactionGroups) {
    return [];
  }

  const ownReactions =
    message.own_reactions?.reduce((acc, reaction) => {
      acc[reaction.type] = true;
      return acc;
    }, {} as Record<string, boolean>) ?? {};

  // I could see the user names available only on the latest_reactions and not on the reaction_groups
  // Hence using the latest_reactions to get the user names for the reactions
  // TODO - @gisheri -  Check if the latest reactions are always available or there is a better way to get the user names
  const users =
    message.latest_reactions?.reduce((acc, { type, user }) => {
      if (user?.name) {
        if (acc[type]) {
          acc[type].push(user.id);
        } else {
          acc[type] = [user.id];
        }
      }
      return acc;
    }, {} as Record<string, string[]>) ?? {};

  const reactions = Object.entries(reactionGroups)
    .map(([reactionName, reaction]) => ({
      name: reactionName,
      count: reaction.count,
      hasOwnReaction: !!ownReactions[reactionName],
      firstReaction: dayjs(reaction.first_reaction_at).valueOf(),
      usersIds: users[reactionName] ?? [],
    }))
    .toSorted((a, b) => a.firstReaction - b.firstReaction);
  return reactions;
};

type StreamUserResponse = UserResponse<DefaultGenerics> & StreamUserMetadata;
/**
 * Constructs a user object from a stream user object
 */
export const constructUserFromStreamUser = (user: StreamUserResponse): User => {
  const { firstName, lastName } = getFirstAndLastName(user.name ?? 'Unknown User');

  return {
    firstName,
    lastName,
    userID: user.id,
    status: constructUserStatusFromStream(user.userStatus, user.weavePresence),
    isDeactivated: !!user.deactivated_at,
  };
};

/**
 *
 * Constructs a stream user object from a user object
 */

export const constructStreamUserFromUser = (user: User): StreamUserResponse => {
  return {
    id: user.userID,
    name: `${user.firstName} ${user.lastName}`,
    deactivated_at: user.isDeactivated ? new Date().toISOString() : undefined,
    ...constructStreamUserMetadataFromUser(user.status.status, user.status.presence),
  };
};

export const constructUserStatusFromStream = (
  streamStatus: StreamUserResponse['userStatus'],
  streamPresence: StreamUserResponse['weavePresence']
): UserStatus => {
  return {
    presence: {
      online: streamPresence ? isUserOnline(streamPresence) : false,
      updatedAt: streamPresence?.updatedAt || '',
      expiresAt: streamPresence?.expiresAt || '',
    },
    status: {
      duration: streamStatus?.statusDuration,
      expiry: streamStatus?.statusExpiration,
      text: streamStatus?.statusText,
      emoji: streamStatus?.emoji,
    },
  };
};

export const constructStreamUserMetadataFromUser = (
  status: User['status']['status'],
  presence: User['status']['presence']
): Pick<StreamUserResponse, 'userStatus' | 'weavePresence'> => {
  return {
    userStatus: {
      statusText: status.text,
      statusDuration: status.duration,
      statusExpiration: status.expiry,
      emoji: status.emoji,
    },
    weavePresence: {
      online: presence.online,
      updatedAt: presence.updatedAt,
      expiresAt: presence.expiresAt,
    },
  };
};

export const getFirstAndLastName = (name: string) => {
  // Just in case to handle bad data
  if (!name || !name.includes(' ')) {
    return { firstName: name ?? '', lastName: '' };
  }

  const [firstName, lastName] = name.split(' ');
  return { firstName, lastName };
};

const isUserOnline = (presence: UserPresence): boolean => {
  if (!presence?.online || !presence?.expiresAt) {
    return false;
  }
  return dayjs(new Date()).isAfter(dayjs(presence?.expiresAt)) ? false : presence?.online;
};

export const isChannelPrivate = (channelId?: string): boolean => {
  if (!channelId) return false;
  return channelId.startsWith(PRIVATE_CHANNEL_PREFIX_DESIGNATION);
};

/**
 * Constructs a conversation object from a stream channel object
 */
export const constructConversationFromStreamChannel = (channel: Channel, currentUserId: string): Conversation => {
  if (!channel.id) {
    throw new Error('Channel id is missing');
  }
  const metadata = channel.data as Channel['data'] & StreamChannelMetadata;
  const isPrivate = isChannelPrivate(channel.id) || metadata?.is_dm;
  const members = Object.values(channel.state.members)
    .filter((user) => !user.banned && user.user_id !== currentUserId)
    .map((member) => member.user)
    .filter((user) => !!user);

  return {
    channelId: channel.id,
    name: !isPrivate && channel.data?.name ? channel.data?.name : members.map((user) => user?.name).join(', '),
    type: isPrivate ? 'DM' : 'Group',
    unreadCount: channel.state.read[currentUserId].unread_messages,
    memberIds: members.filter((member) => !!member.id).map((member) => member.id),
    createdBy: metadata.created_by.name,
    topic: metadata.topic,
    description: metadata.description,
    createdAt: dayjs(metadata.created_at).format('MMMM DD, YYYY'),
    isArchived: !!channel.data?.frozen,
    isHidden: !!channel.data?.hidden,
    usersTyping: Object.values(channel.state.typing)
      .filter((e) => !!e.user_id)
      .map((e) => e.user_id as string),
    lastReadMessageId: channel.state.read[currentUserId].last_read_message_id ?? '',
    lastMessageReadAt: channel.state.read[currentUserId].last_read,
    lastMessageSentAt: channel.lastMessage()?.created_at?.toISOString(),
    members: members.map((member) => constructUserFromStreamUser(member)),
  };
};

/**
 * Constructs a conversation essentials object from a stream channel object
 */

export const constructConversationEssentialsFromStreamChannel = (
  channel: ChannelResponse & {
    topic?: string;
    description?: string;
    last_read_message_id?: string;
    last_read_at?: string;
  },
  currentUser: User,
  unreadCount?: number
): ConversationEssentials => {
  const isPrivate = isChannelPrivate(channel.id);
  const members = channel.members?.filter((user) => !!user.user_id) ?? [];
  const lastReadMessageId = channel.last_read_message_id;
  const lastReadAt = channel.last_read_at ? new Date(channel.last_read_at) : undefined;

  return {
    channelId: channel.id,
    type: channel.id?.startsWith(PRIVATE_CHANNEL_PREFIX_DESIGNATION) ? 'DM' : 'Group',
    topic: channel?.topic ?? '',
    description: channel?.description ?? '',
    unreadCount: unreadCount,
    name:
      !isPrivate && channel.name
        ? channel.name
        : members.reduce((name, user) => {
            if (user.user_id !== currentUser.userID) {
              name += user?.user?.name ?? 'Unnamed User';
            }
            return name;
          }, ''),
    lastReadMessageId,
    lastReadAt,
  };
};

export const getLastMessageEpoch = (messages: Message[]) => {
  return messages.length ? dayjs(messages[messages.length - 1].lastUpdated).valueOf() : 0;
};

/**
 * Constructs an attachment object from a stream attachment object
 */

export const constructAttachmentFromStreamAttachment = (attachment: StreamAttachment): Attachment => {
  const type = ['image', 'video', 'audio', 'giphy'].includes(attachment.type ?? '')
    ? (attachment.type as Attachment['type'])
    : 'file';
  return {
    type,
    title: attachment.title ?? 'Untitled',
    url: attachment.asset_url ?? attachment.image_url ?? attachment.thumb_url ?? '',
  };
};

/**
 * Constructs a stream attachment object from an attachment object
 */

export const constructStreamAttachmentFromAttachment = (attachment: Attachment): StreamAttachment => {
  if (attachment.type === 'giphy') {
    // When the type of attachment is giphy i.e. a GIF then we should return the attachment as is.
    // Because Mobile accepts the attachment as parsed and uploaded in the meta field of use-image-upload hook
    return attachment;
  }
  return {
    type: attachment.type,
    title: attachment.title,
    asset_url: attachment.url,
  };
};

// This method was taken from the old desktop client for conformity
export const generateUniqueName = (friendlyName: string) => {
  return `${friendlyName
    .toLocaleLowerCase()
    .replace(/[^a-zA-Z0-9 ]/g, '')
    .replace(/ /g, '-')
    .slice(0, 50)}-${Date.now().toString()}`;
};

/**
 * Converts a FormatMessageResponse to MessageResponse format for constructMessageFromStreamSearchResult
 */
export const convertFormatMessageToSearchResult = (
  message: FormatMessageResponse
): SearchAPIResponse['results'][number]['message'] => {
  return {
    ...message,
    pinned_at: message.pinned_at ? message.pinned_at.toString() : message.pinned_at,
    created_at: message.created_at.toISOString(),
    updated_at: message.updated_at.toISOString(),
  };
};

export const constructThreadFromStreamThread = (thread: StreamThread, currentUserId: string): Thread => {
  const { channel, replyCount, latestReplies, message, read } = thread;
  const { name: conversationName, members, is_dm } = channel;

  const name =
    conversationName ??
    members
      ?.filter((member) => member.user_id !== currentUserId)
      .map((mem) => mem.user?.name)
      .join(', ') ??
    '';
  const unreadCount = read[currentUserId]?.unread_messages ? read[currentUserId].unread_messages : 0;

  return {
    type: is_dm ? 'DM' : 'Group',
    replyCount,
    name,
    latestReplies: latestReplies.map((message) =>
      constructMessageFromStreamSearchResult(convertFormatMessageToSearchResult(message))
    ),
    parentMessage: constructMessageFromStreamSearchResult(convertFormatMessageToSearchResult(message)),
    unreadCount,
  };
};

export const convertThreadFromMessageReadEvent = (event: Parameters<EventHandler>[0]): ThreadReadData | undefined => {
  if (!event.channel_id || !event.thread?.parent_message_id || !event.last_read_message_id) {
    return undefined;
  }

  return {
    channelId: event.channel_id,
    parentMessageId: event.thread.parent_message_id,
    type: isChannelPrivate(event.channel_id) ? 'DM' : 'Group',
    unreadCount: event.unread_count ?? 0,
    lastReadMessageId: event.last_read_message_id,
  };
};

export const deepEqual = (obj1: unknown, obj2: unknown): boolean => {
  // Check if both are the same reference
  if (obj1 === obj2) return true;

  // Check if either is null or undefined
  if (obj1 == null || obj2 == null) return obj1 === obj2;

  // Check if types are different
  if (typeof obj1 !== typeof obj2) return false;

  // Handle primitive types
  if (typeof obj1 !== 'object') return obj1 === obj2;

  // Handle arrays
  if (Array.isArray(obj1) && Array.isArray(obj2)) {
    if (obj1.length !== obj2.length) return false;
    return obj1.every((item, index) => deepEqual(item, obj2[index]));
  }

  // Handle objects
  if (Array.isArray(obj1) || Array.isArray(obj2)) return false; // One is array, other is not

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  return keys1.every((key) =>
    deepEqual((obj1 as Record<string, unknown>)[key], (obj2 as Record<string, unknown>)[key])
  );
};

export const isUsersMapEqual = (map1: Map<string, User>, map2: Map<string, User>) => {
  if (!map1 || !map2) return false;
  if (map1.size !== map2.size) return false;

  for (const [key, value] of map1) {
    const otherValue = map2.get(key);
    if (!deepEqual(value, otherValue)) {
      return false;
    }
  }
  return true;
};

export const getErrorContext = (error: unknown) => {
  const axiosError = error as {
    config?: { url?: string; method?: string };
    response?: { status?: number };
  };
  if (!axiosError?.config) return undefined;
  return {
    name: 'stream-request',
    context: {
      url: axiosError.config.url,
      method: axiosError.config.method,
      status: axiosError.response?.status,
    },
  };
};
